import { useMemo, useState, useEffect } from "react";
import { marked } from "marked";

const DOCS_CONTENT = `
# Process Documentation

## Hiring Manager Guide

### Creating a Job

Jobs are created from the **Jobs** section in the sidebar. Each job is defined by a markdown description and a set of metadata fields.

**To create a new job:**

1. Navigate to **Jobs > New Job**.
2. Fill in the required fields: title, team, location, and hiring manager.
3. Write the job description in markdown (you can import from a \`.docx\` file). Use the Edit/Preview toggle to check formatting.
4. Optionally configure a **Slack channel** for new application notifications. The hiring manager will also be tagged.
5. Add any custom **application fields** beyond the defaults (name, email, phone, LinkedIn, resume, etc.).
6. Set the status to **Draft** initially, then **Published** when ready to accept applications.

**Job statuses:**

| Status | Description |
|---|---|
| **Draft** | Not visible to candidates. Use while building the listing. |
| **Published** | Live and accepting applications. Candidates can apply via the job link. |
| **Closed** | No longer accepting applications. Existing candidates remain in the pipeline. |

> **Tip:** Once a job is published, share its URL (\`/jobs/<slug>\`) with candidates directly or via job boards.

---

### Assignments

Assignments are take-home exercises sent to candidates as part of the evaluation pipeline.

**Setting up assignments:**

1. Navigate to **Assignments** in the sidebar.
2. Upload a PDF template that describes the assignment.
3. Set a **timeline** (number of days the candidate has to complete it).
4. Define **submission fields**: what the candidate needs to submit back (e.g., repository URL, notes).

**Linking an assignment to a job:**

From the job edit form, select an assignment from the **Assignment** dropdown. When you move a candidate to the "Assignment" stage, they will receive an email with the assignment details and a submission link.

---

### Evaluation Criteria

Evaluation criteria are markdown documents that define how candidates should be scored on a 0 to 10 scale. When mapped to a job, incoming applications are automatically scored by AI.

**Creating evaluation criteria:**

1. Navigate to **Eval Criteria** in the sidebar.
2. Click **New Criteria** and write a markdown document that:
   - Describes what you are evaluating (e.g., technical depth, experience relevance, cultural signals).
   - Defines a clear **0 to 10 scoring system**: what each score range means.
3. On save, AI validates that your document defines a proper scoring system. If it is too vague, you will see what is missing.

**Example structure:**

\`\`\`markdown
# AI Infrastructure Engineer Evaluation

## Scoring System (0-10)
- 9-10: Exceptional. Deep systems expertise, clear personal contributions, strong alignment.
- 7-8: Strong. Solid experience, demonstrates engineering judgment.
- 5-6: Moderate. Relevant background but lacks depth or specificity.
- 3-4: Below average. Limited evidence of hands-on work.
- 0-2: Poor match. No relevant experience or major red flags.

## Evaluation Dimensions

### Technical Depth (40%)
Look for: infrastructure at scale, distributed systems, CI/CD pipelines...

### Relevant Experience (30%)
Look for: years in similar roles, progression, scope of responsibility...

### Cultural Signals (30%)
Look for: authenticity, problem-solving mindset, collaboration evidence...
\`\`\`

**Mapping criteria to a job:**

From the job edit form, select evaluation criteria from the **Evaluation Criteria** dropdown. Only one criteria document can be active per job.

**How scoring works:**

- When a candidate applies to a job with mapped criteria, AI evaluates them using the criteria document, their resume text, and application responses.
- The score (0 to 10) and a short rationale appear on both the candidate list and detail views.
- If you change the criteria content or map different criteria to a job, all candidates still in the **Applied** stage are automatically re-evaluated. Candidates who have moved to other stages keep their original score.

> **Note:** Any employee can create evaluation criteria. Only the creator or an admin can edit or delete a criteria document.

---

### Reviewing Candidates

The **Candidates** page is the primary workspace for reviewing applications.

**Available filters:**

| Filter | Description |
|---|---|
| **Quick search** | Search by name or email |
| **Stage** | Applied, Assignment, Review, Interview, Offer, Talent Pool, Rejected, Withdrawn |
| **Job** | Filter by specific job listing |
| **Hiring Manager** | Filter by assigned hiring manager |
| **Assignment Status** | N/A, Sent, Submitted, Overdue |
| **Flagged** | Show only flagged or non-flagged candidates |
| **Eval Score** | Filter by score range (min/max, 0 to 10) |
| **Date** | Last 24 hours, 3 / 7 / 14 / 30 / 90 days, or a custom range |
| **IDs** | Paste comma-separated candidate IDs to show a specific set |

**Sorting:** Click any sortable column header (Name, Job, Manager, Stage, Assignment, Eval Score, Applied, Updated) to sort ascending or descending.

**Columns displayed:** Name, Job, Hiring Manager, Stage, Assignment Status, Experience (years), Last Title, Eval Score, Latest Note, Phone, LinkedIn, Applied date, Updated date.

Clicking a candidate's name opens their full profile in a new tab.

---

### Candidate Actions

**Single candidate actions** (from the candidate detail view):

| Action | Description |
|---|---|
| **Stage transitions** | Move a candidate forward or backward. Available transitions depend on the current stage. |
| **Reject** | Requires a structured rejection reason from the predefined list, plus an optional note and reference URL. |
| **Move to Talent Pool** | Park a strong candidate for a future role that is not open yet. Requires a note describing why they belong in the pool. Sends a keep-in-touch email instead of a rejection. |
| **Send Assignment** | Moves the candidate to the "Assignment" stage and sends the assignment email. |
| **Add note** | Attach a freeform note visible to the hiring team. URLs are rendered as clickable links. |
| **Flag / Unflag** | Flag a candidate for attention. Flagging requires a note explaining why. |

**Structured rejection reasons:**

1. Synthetic tailoring / JD-derived application
2. Low-authenticity "why this job" response
3. Resume / LinkedIn inconsistency or likely misrepresentation
4. Too vague, low-substance, or non-evidentiary experience
5. No clear evidence of personal contribution
6. Clear role mismatch
7. Weak engineering judgment / poor systems rigor
8. Management-heavy background for an IC role
9. Level mismatch
10. Location / work authorization mismatch
11. Other (freeform)

---

### Bulk Operations

Select multiple candidates using the checkboxes, then use the bulk action buttons that appear at the top of the list.

**Available bulk actions:**

| Action | Description |
|---|---|
| **Bulk Reject** | Requires a rejection reason. Optional note and reference URL apply to all selected candidates. |
| **Bulk Talent Pool** | Requires a note describing future-role fit. Sends a keep-in-touch email to each eligible candidate. |
| **Bulk Send Assignment** | Sends assignments to all selected candidates who are eligible. |

All bulk actions require confirmation before executing.

**Bulk operations via URL:**

For integration with AI agents or Slack workflows, you can construct URLs that pre-populate the candidate list and bulk action parameters:

\`\`\`
/candidates?ids=1,5,12&note=Reviewed+in+weekly+sync&ref=https://slack.com/archives/C123/p456
\`\`\`

| Parameter | Description |
|---|---|
| \`ids\` | Comma-separated candidate IDs to display |
| \`note\` | Pre-filled note for the bulk action |
| \`ref\` | Pre-filled reference URL (e.g., a Slack thread) |

When IDs are provided, a banner shows the active filter with the note and reference URL context.

---

## Candidate Experience

### Receiving a Job Link

Candidates receive a direct link to the job listing (e.g., \`/jobs/software-engineer-ai-infrastructure-reliability\`). The page displays the full description, requirements, and an **Apply** button.

If the candidate is not signed in, they are redirected to the login page first. Authentication uses Microsoft OAuth: candidates sign in with their Microsoft account and are automatically registered as applicants.

---

### Applying

**The application flow:**

1. Click **Apply** on the job page.
2. Upload a resume (PDF only, max 10 MB). The system automatically extracts contact information, years of experience, and last title from the resume using AI.
3. Pre-filled fields (name, email, phone, LinkedIn) can be corrected.
4. Fill in required fields: available start date, US work authorization, and "Why are you a good fit?"
5. Any custom application fields defined by the hiring manager appear here as well.
6. On submission, a confirmation page is displayed.

**What happens behind the scenes:**

- Resume text is extracted and stored for search and evaluation.
- If the job has mapped evaluation criteria, the candidate is automatically scored by AI.
- The hiring manager is notified via email. If a Slack channel is configured, a notification is posted there.
- Candidates can only have one active application at a time across all jobs.

---

### Assignment Submission

When a candidate is moved to the "Assignment" stage, they receive an email with:

- The assignment brief (from the PDF template).
- A deadline (based on the configured timeline).
- A link to submit their work.

The submission form includes the fields defined in the assignment template (e.g., repository URL, notes). After submitting, the assignment status updates to "Submitted" on the hiring team's dashboard.

If the deadline passes without submission, the assignment status shows as **Overdue**.

---

### Status and Withdrawal

Candidates can view their application status at any time by visiting the application portal. The portal shows their current stage and the job they applied to.

**Self-withdrawal:** Candidates can withdraw their application at any point before a final decision. The hiring manager is notified via email when a candidate withdraws.

---

### Account and Authentication

- Authentication is via **Microsoft OAuth**. Candidates sign in with their existing Microsoft account.
- A **Sign out** button is always visible in the header when the candidate is authenticated, even if their account data has been removed from the system.
- Candidates are automatically redirected to their active application if they have one, or to the landing page if they don't.
`;

interface TocEntry {
  id: string;
  text: string;
  level: number;
}

function buildToc(markdown: string): TocEntry[] {
  const entries: TocEntry[] = [];
  const re = /^(#{2,3})\s+(.+)$/gm;
  let match: RegExpExecArray | null;
  while ((match = re.exec(markdown)) !== null) {
    const level = match[1]!.length;
    const text = match[2]!;
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    entries.push({ id, text, level });
  }
  return entries;
}

function addHeadingIds(html: string): string {
  return html.replace(/<h([23])>(.+?)<\/h[23]>/g, (_match, level, text) => {
    const id = text
      .replace(/<[^>]+>/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    return `<h${level} id="${id}">${text}</h${level}>`;
  });
}

export function DocsPage() {
  const toc = useMemo(() => buildToc(DOCS_CONTENT), []);
  const html = useMemo(() => {
    const raw = marked.parse(DOCS_CONTENT, { async: false }) as string;
    return addHeadingIds(raw);
  }, []);

  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 },
    );

    for (const { id } of toc) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [toc]);

  return (
    <div className="flex gap-10">
      <article
        className="docs-prose flex-1 min-w-0 pb-16"
        dangerouslySetInnerHTML={{ __html: html }}
      />

      <nav className="hidden xl:block w-60 shrink-0">
        <div className="sticky top-6">
          <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-4">
            On this page
          </h4>
          <ul className="space-y-0.5 text-[13px] border-l border-gray-200">
            {toc.map((entry) => (
              <li key={entry.id}>
                <a
                  href={`#${entry.id}`}
                  className={`block py-1 transition-colors duration-150 ${
                    entry.level === 3 ? "pl-7" : "pl-4"
                  } ${
                    activeId === entry.id
                      ? "text-primary-700 border-l-2 border-primary-500 -ml-px font-medium"
                      : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  {entry.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </div>
  );
}
